/**
 * 診断モジュール
 * AI鑑定の実行と診断結果の表示
 */

import { state } from './state';
import { addMsg, setInputArea, clearInputArea, setProgress, formatText, typeHtml, scrollChatToBottom } from './chat';
import { getUserNameForDisplay } from './profile';

const thinkingMessages = [
  '星の配置とエレメントを重ねています…',
  '今のテーマに近い石の意味を照らし合わせています…',
  'おすすめできるブレスレット候補を確認しています…',
  '診断結果を読みやすい言葉に整えています…',
];

function rotateThinking(): void {
  const el = document.getElementById('thinkingText');
  if (!el) return;
  state.thinkingIndex = (state.thinkingIndex + 1) % thinkingMessages.length;
  el.textContent = thinkingMessages[state.thinkingIndex];
}

/** 診断を実行 */
export async function executeDiagnose(): Promise<void> {
  setProgress(3, 4, '導きの石を選定');
  const nameForDisplay = getUserNameForDisplay();
  let problem = (document.getElementById('problemText') as HTMLTextAreaElement)?.value || '';

  if (!problem) {
    problem = `具体的なご相談内容は書かれていないため、今の${nameForDisplay}の全体の流れや、これから大切にしたいテーマがわかるような全体運を中心に読み解いてください。`;
  }

  addMsg(problem, true);
  state.formData.problem = problem;

  const date = state.formData.birth?.date;
  if (!date) {
    addMsg('生年月日は必須です', false);
    return;
  }

  // 送信後は入力欄をすぐに消し、メッセージが流れる間ずっと残らないようにする
  clearInputArea();

  await addMsg('わかりました。ありがとうございます。\nこの内容で星と石の流れを読み解いていきます。\n\n入力内容は、今のテーマを見つめるための手がかりとして丁寧に扱います。', false);

  setInputArea(`
    <div class="loading">
      <div class="spinner"></div>
      <p id="thinkingText">星の配置とエレメントを重ねています…</p>
      <div class="reading-steps">
        <span>星読み</span>
        <span>悩みの整理</span>
        <span>天然石選定</span>
      </div>
    </div>
  `);

  const thinkingTimer = setInterval(rotateThinking, 2000);

  try {
    state.formData.line_user_id = window.LINE_USER_ID ?? state.userId ?? undefined;
    const res = await fetch('/api/diagnose', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(state.formData),
    });

    if (!res.ok) {
      throw new Error(`HTTP error ${res.status}`);
    }

    const json = await res.json();
    if (json.error) {
      clearInterval(thinkingTimer);
      addMsg(`エラーが発生しました: ${json.error}`, false);
      clearInputArea();
      return;
    }

    window.diagnosisId = json.diagnosis_id;
    state.productCandidates = json.recommendations || [];
    state.selectedProductIndex = state.productCandidates.length > 0 ? 0 : null;

    clearInterval(thinkingTimer);
    state.divinationResult = json;
    displayDivinationResult(json);
  } catch {
    clearInterval(thinkingTimer);
    addMsg('通信エラーが発生しました', false);
    clearInputArea();
  }
}

/** オラクルカードを表示 */
async function showOracleCard(card: { image_url?: string; name: string; is_upright: boolean; colors?: Record<string, string> }): Promise<void> {
  await addMsg('カードをシャッフルしています…', false);
  await new Promise(r => setTimeout(r, 1200));
  await addMsg('1枚引きます…', false);
  await new Promise(r => setTimeout(r, 1000));

  // 画像があれば表示、なければCSSグラデーションカード
  const colors = card.colors || {};
  const gradient = colors.gradient || 'linear-gradient(135deg, #1a0533 0%, #4a148c 40%, #ab47bc 100%)';
  const hasImage = card.image_url && card.image_url.length > 10;

  const imageHtml = hasImage
    ? `<img src="${card.image_url}" class="section-image" style="width:220px;margin:12px auto;display:block;border-radius:12px;" onload="this.classList.add('loaded')" onerror="this.parentElement.querySelector('.card-fallback').style.display='flex';this.style.display='none';">`
    : '';

  const fallbackStyle = hasImage ? 'display:none;' : 'display:flex;';

  const cardHtml = `
    <div class="msg bot">
      <div class="result-section" style="text-align:center;">
        <h3>🎴 オラクルカード</h3>
        ${imageHtml}
        <div class="card-fallback" style="${fallbackStyle}width:220px;height:320px;margin:12px auto;border-radius:16px;background:${gradient};align-items:center;justify-content:center;flex-direction:column;box-shadow:0 8px 24px rgba(0,0,0,0.3),inset 0 1px 0 rgba(255,255,255,0.15);border:2px solid rgba(255,215,0,0.4);">
          <div style="font-size:48px;margin-bottom:12px;">🔮</div>
          <div style="color:#fff;font-size:18px;font-weight:bold;text-shadow:0 2px 4px rgba(0,0,0,0.5);">${card.name}</div>
          <div style="color:rgba(255,255,255,0.8);font-size:14px;margin-top:6px;">${card.is_upright ? '正位置' : '逆位置'}</div>
        </div>
        <p style="font-size:17px;font-weight:bold;margin-top:10px;">
          ${card.name} ${card.is_upright ? '（正位置）' : '（逆位置）'}
        </p>
      </div>
    </div>
  `;

  const box = document.getElementById('chatBox');
  if (box) {
    box.insertAdjacentHTML('beforeend', cardHtml);
    scrollChatToBottom();
  }
}

/** セクション内タイプエフェクト（改行・太字をタイプ中から描画） */
function typeIntoElement(element: HTMLElement, rawText: string, speed = 20): Promise<void> {
  // \n → <br> 変換してから formatText（** → <strong>）を適用
  const html = formatText((rawText || '').replace(/\n/g, '<br>'));
  return typeHtml(element, html, speed);
}

/** 「。」の直後で文を区切り、文の途中では切らずに maxLen 文字程度へまとめる */
function chunkBySentence(text: string, maxLen = 50): string[] {
  const sentences = text.split(/(?<=。)/).map(s => s.trim()).filter(Boolean);
  const chunks: string[] = [];
  let current = '';
  for (const sentence of sentences) {
    if (current && (current + sentence).length > maxLen) {
      chunks.push(current);
      current = sentence;
    } else {
      current += sentence;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

/** 空行（\n\n）の段落分けを尊重しつつ、さらに1吹き出し50文字程度の塊に分割する。
 *  短すぎる吹き出しの連発は読んでいて気持ち悪くなるため、文の区切りを保ったまま
 *  ある程度の分量でまとめるのがポイント。 */
function splitIntoParagraphs(text: string): string[] {
  const paragraphs = (text || '')
    .split(/\n{2,}/)
    .map(p => p.trim())
    .filter(Boolean);
  return paragraphs.flatMap(p => chunkBySentence(p));
}

/** セクションカードを1枚チャットに追加し、段落ごとに順番にタイプアニメーションを流す */
async function appendSection(
  chatBox: HTMLElement,
  title: string,
  text: string,
  image?: string
): Promise<void> {
  const wrapper = document.createElement('div');
  wrapper.className = 'msg bot';
  const inner = document.createElement('div');
  inner.className = 'result-section';
  const h3 = document.createElement('h3');
  h3.textContent = title;
  inner.appendChild(h3);
  if (image) {
    const img = document.createElement('img');
    img.src = image;
    img.className = 'section-image';
    img.alt = title;
    img.onload = () => img.classList.add('loaded');
    img.onerror = () => img.style.display = 'none';
    inner.appendChild(img);
  }
  wrapper.appendChild(inner);
  chatBox.appendChild(wrapper);
  scrollChatToBottom();

  const paragraphs = splitIntoParagraphs(text);
  for (let i = 0; i < paragraphs.length; i++) {
    const p = document.createElement('p');
    inner.appendChild(p);
    await typeIntoElement(p, paragraphs[i], 18);
    if (i < paragraphs.length - 1) {
      await new Promise(r => setTimeout(r, 750));
    }
  }
}

/** 一致率バッジと「なぜこの石か」の一文で、確定した石を発表する */
async function showStoneReveal(stoneName: string, matchScore: number | null, whyText: string): Promise<void> {
  const chatBox = document.getElementById('chatBox');
  if (!chatBox) return;

  const wrapper = document.createElement('div');
  wrapper.className = 'msg bot';
  const inner = document.createElement('div');
  inner.className = 'result-section';

  const h3 = document.createElement('h3');
  h3.textContent = `導きの石：${stoneName}`;
  inner.appendChild(h3);

  if (matchScore !== null) {
    const pct = Math.max(0, Math.min(100, matchScore));
    const scoreWrap = document.createElement('div');
    scoreWrap.className = 'match-score';
    scoreWrap.innerHTML = `
      <div class="match-score__label">
        <span>あなたとの一致率</span>
        <span class="match-score__pct">${pct}%</span>
      </div>
      <div class="match-score__bar"><div class="match-score__fill" style="width:${pct}%"></div></div>
    `;
    inner.appendChild(scoreWrap);
  }

  wrapper.appendChild(inner);
  chatBox.appendChild(wrapper);
  scrollChatToBottom();

  if (whyText) {
    const p = document.createElement('p');
    p.className = 'why-this-stone';
    inner.appendChild(p);
    await typeIntoElement(p, whyText, 22);
    await new Promise(r => setTimeout(r, 450));
  }
}

/** 診断結果を表示（3グループ構成・タップ回数削減版） */
async function displayDivinationResult(result: Record<string, unknown>): Promise<void> {
  setProgress(3, 4, '導きの石を選定');
  clearInputArea();
  const nameForDisplay = getUserNameForDisplay();
  const recommendations = (result.recommendations as Array<{ stones?: string[]; product_name?: string }>) || [];
  const stoneName =
    (result.stone_name as string) ||
    recommendations[0]?.stones?.[0] ||
    'あなたの石';
  const chatBox = document.getElementById('chatBox');
  if (!chatBox) return;
  const chatBoxEl: HTMLElement = chatBox;

  const images = (result.images || {}) as Record<string, string>;
  const oracleCard = result.oracle_card as { image_url: string; name: string; is_upright: boolean } | undefined;

  // ===== グループ1: 星の地図 + これまでの流れ =====
  async function showGroup1(): Promise<void> {
    await addMsg(`診断結果が出ました。\n星の配置とエレメントの流れをもとに、${nameForDisplay}の今を順番に読み解いていきます。`, false);

    // destiny_map テキストまたは画像があれば表示
    if (result.destiny_map || images.destiny_scene) {
      await appendSection(chatBoxEl, '星の地図', (result.destiny_map as string) || '', images.destiny_scene);
    }
    if (result.past) {
      await appendSection(chatBoxEl, 'これまでの流れ', result.past as string);
    }

    setInputArea(`
      <button class="btn" onclick="showNextSection()">続きを読む　→　今のあなたへのメッセージ</button>
    `);
  }

  // ===== グループ2: 今と未来 + エレメント + オラクルカード =====
  async function showGroup2(): Promise<void> {
    await addMsg('ここからは、今の状態とこれからの流れを見ていきます。', false);

    if (result.present_future) {
      await appendSection(chatBoxEl, '今と、これからの流れ', result.present_future as string);
    }
    if (result.element_diagnosis) {
      await appendSection(chatBoxEl, 'エネルギーのバランス', result.element_diagnosis as string, images.element_balance);
    }

    if (oracleCard) {
      await addMsg('最後にオラクルカードを引いて、導きの声を聞いてみましょう。', false);
      await showOracleCard(oracleCard);
      if (result.oracle_message) {
        await appendSection(chatBoxEl, 'オラクルからのメッセージ', result.oracle_message as string);
      }
    }

    setInputArea(`
      <button class="btn" onclick="showNextSection()">続きを読む　→　石のメッセージ</button>
    `);
  }

  // ===== グループ3: 石のサポート + アドバイス + 商品へ =====
  async function showGroup3(): Promise<void> {
    await addMsg('最後に、今のあなたを整える石と、そのサポートをお伝えします。', false);

    const matchScore = typeof result.match_score === 'number' ? Math.round(result.match_score as number) : null;
    const whyThisStone = (result.why_this_stone as string) || '';
    if (matchScore !== null || whyThisStone) {
      await showStoneReveal(stoneName, matchScore, whyThisStone);
    }

    if (result.bracelet_proposal) {
      await appendSection(chatBoxEl, `${stoneName}`, result.bracelet_proposal as string, images.beads);
    }
    if (result.stone_support_message) {
      await appendSection(chatBoxEl, `${stoneName}からのメッセージ`, result.stone_support_message as string);
    }

    // 自分に贈る言葉・ラッキーカラー・アドバイスをまとめて1メッセージに
    const extras: string[] = [];
    if (result.affirmation) extras.push(`今日、自分に贈る言葉\n**${result.affirmation}**`);
    if (result.lucky_color) extras.push(`今日のラッキーカラー：**${result.lucky_color}**`);
    if (result.daily_advice) {
      const adviceList = (result.daily_advice as string).split(',').map((a: string) => `・ ${a.trim()}`).join('\n');
      extras.push(`📝 今日からできること\n${adviceList}`);
    }
    if (extras.length > 0) {
      await addMsg(extras.join('\n\n'), false);
    }

    await addMsg(
      `今回の診断で、${nameForDisplay}の軸となる石は **${stoneName}** です。\n\n`
      + `ふと迷ったとき、心が揺れたとき、そっと手首に触れてみてください。\n`
      + `**${stoneName}** の静かなエネルギーが、あなた本来のリズムを思い出させてくれます。`,
      false
    );

    setInputArea(`
      <button class="btn" onclick="showProductCandidates()">💎 あなたへのおすすめブレスレットを見る</button>
    `);
  }

  // ===== グループ制御 =====
  let groupIndex = 0;
  const groups = [showGroup1, showGroup2, showGroup3];

  async function showNextSection(): Promise<void> {
    const labels = ['続きを読む　→　今のあなたへのメッセージ', '続きを読む　→　石のメッセージ'];
    await addMsg(labels[groupIndex] || '続きを読む', true);
    groupIndex++;
    await groups[groupIndex]?.();
  }

  window.showNextSection = showNextSection;
  await groups[0]();
}
